import React from 'react';

import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { css, cx } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import { Button } from 'antd';
import { theme } from 'theme';
import { fontWeights } from 'theme/typography';

import { ActionButton } from 'components/Button/ActionButton';
import { MiniTable } from 'components/MiniTable';
import { Modal } from 'components/Modal/Modal';
import { TextArea } from 'components/TextArea/TextArea';

import { useCoreApi } from 'hooks/useApi';

import { coreApiFetch } from 'utils/api';
import { CloseableModalProps, showAsyncModal } from 'utils/asyncModal';
import { captureAndShowError } from 'utils/error';
import { formatDateShorthand } from 'utils/formatting';

import { NewLineToParagraph } from '../NewLineToParagraph';
import { ExpandableTextBlock } from './ExpandableTextBlock';

interface ItemLocationNotesPanelContentProps {
  itemLocationTitle: string;
  itemUid: string;
  locationId: string;
}

export function ItemLocationNotesPanelContent({
  itemLocationTitle,
  itemUid,
  locationId,
}: ItemLocationNotesPanelContentProps) {
  const { data, isLoading, reload } = useCoreApi(schemas.notes.getItemLocationNotes, {
    queryParams: {
      itemUid,
      locationId,
      limit: 1,
    },
  });

  const currentNote = data?.items[0];

  const handleModal = async () => {
    await showAsyncModal(ItemLocationNotesEditModal, {
      title: `${itemLocationTitle}`,
      itemUid,
      locationId,
      onUpdated: reload,
    });
  };

  if (isLoading) return <MiniTable columns={[]} data={[]} isLoading />;

  return (
    <div
      className={css`
        margin-top: 10px;
      `}
    >
      {currentNote ? (
        <div className={cx('NoteCell')}>
          <div>
            <div
              className={css`
                color: ${theme.colors.neutral[900]};
              `}
            >
              <ExpandableTextBlock
                text={currentNote.notes}
                rows={2}
                expandNlToBreaks
                key={`${currentNote.updatedAt}`}
              />
            </div>
            <ActionButton
              label={
                <>
                  <EditOutlined /> Edit
                </>
              }
              onClick={handleModal}
            />
          </div>
        </div>
      ) : (
        <>
          <MiniTable columns={[]} data={[]} />
          <ActionButton
            label={
              <>
                <EditOutlined /> Add
              </>
            }
            onClick={handleModal}
          />
        </>
      )}
    </div>
  );
}

export interface ItemLocationNotesEditModalProps extends CloseableModalProps<void> {
  title: string;
  itemUid: string;
  locationId: string;
  onUpdated: () => void;
  onClose: () => void;
}

export function ItemLocationNotesEditModal({
  title,
  itemUid,
  locationId,
  onUpdated,
  onClose,
}: ItemLocationNotesEditModalProps) {
  const [activeNoteIndex, setActiveNoteIndex] = React.useState<number>(0);
  const [updatedNoteText, setUpdatedNoteText] = React.useState<string>('');
  const [confirmingDeleteId, setConfirmingDeleteId] = React.useState<string | null>(null);
  const [historyMaxItems, setHistoryMaxItems] = React.useState<number>(10);

  const { data: history, reload: reloadHistory } = useCoreApi(schemas.notes.getItemLocationNotes, {
    queryParams: {
      itemUid,
      locationId,
      limit: historyMaxItems,
    },
  });

  const handleFetchMoreHistory = async () => {
    try {
      const itemCount = historyMaxItems >= (history?.totalCount || 0) ? history?.totalCount || 0 : historyMaxItems + 10;
      setHistoryMaxItems(itemCount);
      if (itemCount >= (history?.totalCount || 0)) {
        return;
      }
      await reloadHistory();
    } catch (error) {
      captureAndShowError(error, 'Error fetching note history');
    }
  };

  const noteHistory = history?.items || [];
  const activeNote = noteHistory[activeNoteIndex];

  const handleCreateNote = async (noteText: string) => {
    try {
      await coreApiFetch(schemas.notes.postUpdateItemLocationNotes, {
        bodyParams: {
          itemUid,
          locationId,
          notes: noteText,
        },
      });
      setUpdatedNoteText('');
      await reloadHistory();
      onUpdated();
      setActiveNoteIndex(0);
    } catch (error) {
      captureAndShowError(error, 'Error updating notes');
    }
  };

  const handleDeleteNote = async (noteHistoryId: string) => {
    try {
      await coreApiFetch(schemas.notes.deleteItemLocationNotes, {
        queryParams: {
          itemLocationNotesId: `${noteHistoryId}`,
        },
      });
      setConfirmingDeleteId(null);
      await reloadHistory();
      setActiveNoteIndex(0);
    } catch (error) {
      captureAndShowError(error, 'Error deleting note history item');
    }
  };

  return (
    <Modal
      visible
      width={800}
      onCancel={() => {
        onClose();
      }}
      title={`Item Location Notes - ${title}`}
      destroyOnClose
      footer={
        <>
          <Button onClick={() => onClose()}>Cancel</Button>
          <Button type="primary" onClick={() => handleCreateNote(updatedNoteText)}>
            Save
          </Button>
        </>
      }
    >
      <div
        className={css`
          display: flex;
          gap: 16px;
          height: 350px;
        `}
      >
        {/* Left side - Current note display */}
        <div
          className={css`
            flex: 1;
            display: flex;
            flex-direction: column;
          `}
        >
          <div
            className={css`
              font-weight: ${fontWeights.semibold};
              margin-bottom: 8px;
            `}
          >
            Notes
          </div>
          <div
            className={css`
              overflow-y: auto;
            `}
          >
            {activeNote ? <NewLineToParagraph note={activeNote.notes} /> : ''}
          </div>
        </div>

        {/* Right side - History */}
        <div
          className={css`
            flex: 1;
            border-left: 1px solid ${theme.colors.neutral[200]};
            padding-left: 16px;
          `}
        >
          <div
            className={css`
              font-weight: ${fontWeights.semibold};
              margin-bottom: 8px;
            `}
          >
            History
          </div>
          <div
            className={css`
              max-height: 400px;
              overflow-y: auto;
            `}
          >
            {noteHistory.map((note, idx) => (
              <div
                key={note.itemLocationNotesId}
                className={css`
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  margin-top: 4px;
                `}
              >
                <div
                  className={css`
                    display: flex;
                    gap: 8px;
                  `}
                >
                  <ActionButton
                    label={
                      <>
                        <EyeOutlined /> View
                      </>
                    }
                    onClick={() => setActiveNoteIndex(idx)}
                  />
                  {confirmingDeleteId === note.itemLocationNotesId ? (
                    <>
                      <ActionButton
                        className={css`
                          color: ${theme.colors.danger[500]};
                          &:hover {
                            color: ${theme.colors.danger[700]};
                          }
                        `}
                        label={
                          <>
                            <CheckOutlined /> Yes
                          </>
                        }
                        onClick={() => handleDeleteNote(note.itemLocationNotesId)}
                      />
                      <ActionButton
                        className={css`
                          color: ${theme.colors.success[500]};
                          &:hover {
                            color: ${theme.colors.success[700]};
                          }
                        `}
                        label={
                          <>
                            <CloseOutlined /> No
                          </>
                        }
                        onClick={() => setConfirmingDeleteId(null)}
                      />
                    </>
                  ) : (
                    idx !== 0 && (
                      <ActionButton
                        label={
                          <>
                            <DeleteOutlined /> Delete
                          </>
                        }
                        onClick={() => setConfirmingDeleteId(note.itemLocationNotesId)}
                      />
                    )
                  )}
                </div>
                <div
                  className={css`
                    display: flex;
                    gap: 4px;
                    align-items: center;
                  `}
                >
                  <span
                    className={css`
                      color: ${idx === activeNoteIndex ? theme.colors.neutral[900] : theme.colors.neutral[500]};
                      font-style: italic;
                      font-weight: ${theme.typography.pMedium.fontWeight};
                    `}
                  >
                    {note.updatedBy} - {formatDateShorthand(note.updatedAt)}
                  </span>
                  <span
                    className={css`
                      font-weight: ${theme.typography.pLarge.fontWeight};
                      min-width: 16px;
                      text-align: center;
                    `}
                  >
                    {idx === 0 ? '*' : ''}
                  </span>
                </div>
              </div>
            ))}
            <div
              className={css`
                margin-top: 8px;
                font-size: 12px;
                color: ${theme.colors.neutral[500]};
                display: flex;
                justify-content: space-between;
                align-items: center;
              `}
            >
              <span>
                <span
                  className={css`
                    font-weight: ${theme.typography.pLarge.fontWeight};
                    min-width: 16px;
                    text-align: center;
                  `}
                >
                  *
                </span>{' '}
                Current Note
              </span>
              <span
                className={css`
                  text-align: right;
                  flex: 1;
                `}
              >
                {history?.items?.length} of {history?.totalCount} notes
              </span>
            </div>
            <div
              className={css`
                font-weight: ${theme.typography.pLarge.fontWeight};
                min-width: 16px;
                text-align: center;
              `}
            >
              {history && history.items.length < (history.totalCount || 0) && (
                <ActionButton
                  label={
                    <>
                      <PlusOutlined /> Load More
                    </>
                  }
                  onClick={handleFetchMoreHistory}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Note section */}
      <div
        className={css`
          margin-top: 16px;
        `}
      >
        <div
          className={css`
            font-weight: ${fontWeights.semibold};
            margin-bottom: 8px;
          `}
        >
          Add Note
        </div>
        <TextArea
          value={updatedNoteText}
          onChange={(e) => setUpdatedNoteText(e.target.value)}
          placeholder="Enter notes for this item/location. These notes will be visible in the planning side pane and can be used to communicate important information about this item at this location."
          rows={4}
        />
      </div>
    </Modal>
  );
}
